import Axios, {AxiosRequestHeaders} from 'axios'
import {ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY} from 'sr/constants/common'
import {getCookieValue, setCookieValue} from '../helper'
import { deleteCookies } from 'sr/helpers/functions/deleteCookies'
import { toast } from 'react-toastify'

const axios = Axios.create({
  baseURL: process.env.REACT_APP_API_URL,
})

const refreshAxios = Axios.create({
  baseURL: process.env.REACT_APP_API_URL,
})

// axios.defaults.headers.common['Access-Control-Allow-Origin'] = '*'
axios.defaults.headers.post['Content-Type'] = 'application/json'

const REFRESH_ENDPOINTS = [
  {method: 'get', url: '/admin/auth/refreshjwt'},
  {method: 'post', url: '/admin/auth/refresh-token'},
  {method: 'get', url: '/auth/refreshjwt'},
  {method: 'post', url: '/auth/refresh-token'},
] as const

let refreshTokenPromise: Promise<{accessToken: string; refreshToken?: string} | null> | null = null

const getAccessTokenFromRefreshResponse = (data: any) => {
  return data?.jwt || data?.accessToken || data?.auth?.jwt || data?.data?.jwt || data?.data?.auth?.jwt
}

const getRefreshTokenFromRefreshResponse = (data: any) => {
  return (
    data?.refreshToken ||
    data?.jwt_refresh ||
    data?.auth?.jwt_refresh ||
    data?.data?.refreshToken ||
    data?.data?.jwt_refresh ||
    data?.data?.auth?.jwt_refresh
  )
}

const refreshAccessToken = async (refreshToken: string) => {
  for (const endpoint of REFRESH_ENDPOINTS) {
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }

      const response =
        endpoint.method === 'get'
          ? await refreshAxios.get(endpoint.url, config)
          : await refreshAxios.post(endpoint.url, {refreshToken}, config)

      const accessToken = getAccessTokenFromRefreshResponse(response.data)
      if (accessToken) {
        return {
          accessToken,
          refreshToken: getRefreshTokenFromRefreshResponse(response.data),
        }
      }
    } catch (refreshError) {
      console.warn(`[AUTH] Refresh failed for ${endpoint.url}`, refreshError)
    }
  }

  return null
}

axios.interceptors.request.use(
  async (config) => {
    const copy = {...config}

    // Check for Read-Only mode before allowing write operations
    const isReadOnly = localStorage.getItem('isReadOnly') === 'true'
    const isWriteOperation = ['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase() || '')
    
    // Exempt auth endpoints from Read-Only mode (login, logout, password verify, etc.)
    const authExemptPaths = [
      '/auth',
      '/auth/login',
      '/auth/logout',
      '/auth/verify-password',
      '/auth/forgot',
      '/auth/reset',
      '/auth/refreshjwt',
      '/auth/refresh-token',
      '/auth/sendotp',
      '/admin/auth',
      '/admin/auth/login',
      '/admin/auth/logout',
      '/admin/auth/refreshjwt',
      '/admin/auth/refresh-token',
      '/admin/auth/change-password',
      '/admin/auth/profile-picture',
      '/admin/auth/verify-2fa',
      '/admin/auth/2fa/setup',
      '/admin/auth/2fa/verify',
      '/admin/auth/2fa/disable',
      '/admin/auth/2fa/status',
      '/admin/auth/2fa/regenerate-backup-codes',
      '/admin/audit/log',
      '/admin/audit/purge',
      '/admin/impersonate'
    ]
    const isAuthEndpoint = authExemptPaths.some(path => config.url?.includes(path))

    if (isReadOnly && isWriteOperation && !isAuthEndpoint) {
      // Block the request
      console.warn('[READ-ONLY MODE] Blocked write operation:', config.method?.toUpperCase(), config.url)
      toast.error('Write operations are disabled in Read-Only mode. Please switch to Update mode.')
      return Promise.reject({
        config,
        message: 'Write operations disabled in Read-Only mode',
        isReadOnlyBlock: true
      })
    }

    if (typeof localStorage !== 'undefined') {
      const accessToken = getCookieValue(ACCESS_TOKEN_KEY)
      if (accessToken && copy.headers && !(copy.headers as AxiosRequestHeaders).Authorization) {
        ;(copy.headers as AxiosRequestHeaders).Authorization = `Bearer ${accessToken}`
      }
    }

    // ;(copy.headers as AxiosRequestHeaders).Authorization = `Bearer ${accessToken}`

    return config
  },
  async (error) => Promise.reject(error)
)

axios.interceptors.response.use(
  async (response) => {
   if (response.data.error == null) {
      return response
    }
    return Promise.reject(response)
  },

  async (error) => {
    const originalRequest = error.config;
    const isRefreshRequest = REFRESH_ENDPOINTS.some((endpoint) =>
      originalRequest?.url?.includes(endpoint.url)
    );
    
    // Don't attempt token refresh for login/auth endpoints - just return the error
    const isLoginRequest = originalRequest?.url?.includes('/admin/auth/login') || 
                           originalRequest?.url?.includes('/auth/login') ||
                           originalRequest?.url?.includes('/admin/auth/forgot-password') ||
                           originalRequest?.url?.includes('/admin/auth/reset-password');

    // For login requests, just return the error without trying to refresh
    if (isLoginRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry && !isRefreshRequest) {
      originalRequest._retry = true;
      try {
        const refreshToken = getCookieValue(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          deleteCookies(REFRESH_TOKEN_KEY);
          deleteCookies(ACCESS_TOKEN_KEY);
          window.location.href = '/auth';
          return Promise.reject(error);
        }

        if (!refreshTokenPromise) {
          refreshTokenPromise = refreshAccessToken(refreshToken).finally(() => {
            refreshTokenPromise = null;
          });
        }

        const refreshedTokens = await refreshTokenPromise;

        if (refreshedTokens?.accessToken) {
          const accessTokenExpiresInDays = 1;
          const refreshTokenExpiresInDays = 7;

          setCookieValue(ACCESS_TOKEN_KEY, refreshedTokens.accessToken, accessTokenExpiresInDays);
          if (refreshedTokens.refreshToken) {
            setCookieValue(REFRESH_TOKEN_KEY, refreshedTokens.refreshToken, refreshTokenExpiresInDays);
          }

          originalRequest.headers.Authorization = `Bearer ${refreshedTokens.accessToken}`;
          return axios(originalRequest);
        } else {
          deleteCookies(REFRESH_TOKEN_KEY);
          deleteCookies(ACCESS_TOKEN_KEY);
          window.location.href = '/auth';
          return Promise.reject(error);
        }
      } catch (err) {
        deleteCookies(REFRESH_TOKEN_KEY);
        deleteCookies(ACCESS_TOKEN_KEY);
        window.location.href = '/auth';
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }

)


export default axios
